import { useEffect, useRef } from "react";
import { useAppSelector } from "../../../../common/hooks/hooks";
import { RootState } from "../../../../app/store";

/**
 * A custom hook that synchronizes the product archive's filters, pagination, and UI state from Redux
 * to the browser's URL query string. It can also be configured to preserve specific existing query parameters.
 * @param {string[]} [preserveParams] - An optional array of query parameter keys to preserve from the current URL.
 */
export const useSyncProductsArchiveStateToUrl = (
  preserveParams: string[] = []
) => {
  const filters = useAppSelector((state: RootState) => state.product.filters);
  const pagination = useAppSelector(
    (state: RootState) => state.product.pagination
  );
  const ui = useAppSelector((state: RootState) => state.product.ui);
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Skip the first run to prevent an unnecessary URL update on initial load,
    // as the state is already initialized from the URL.
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const newQueryParts: string[] = [];
    const currentParams = new URLSearchParams(window.location.search);

    // --- 1. Preserve specified global parameters ---
    // The hook is now agnostic about what these params are.
    // It just preserves them if they exist.
    preserveParams.forEach((paramName) => {
      if (currentParams.has(paramName)) {
        newQueryParts.push(
          `${paramName}=${encodeURIComponent(currentParams.get(paramName)!)}`
        );
      }
    });

    // --- 2. Sync All Product-Specific Filters ---
    if (filters.search) {
      newQueryParts.push(`productSearch=${encodeURIComponent(filters.search)}`);
    }
    if (filters.onSale) {
      newQueryParts.push("productOnSale=true");
    }
    if (filters.inStock) {
      newQueryParts.push("productInStock=true");
    }
    if (filters.orderby !== "date") {
      newQueryParts.push(`productOrderby=${filters.orderby}`);
    }
    if (filters.minPrice !== null) {
      newQueryParts.push(`productMinPrice=${filters.minPrice}`);
    }
    if (filters.maxPrice !== null) {
      newQueryParts.push(`productMaxPrice=${filters.maxPrice}`);
    }
    if (filters.category.length > 0) {
      newQueryParts.push(`productCategory=${filters.category.join(",")}`);
    }
    if (filters.tag.length > 0) {
      newQueryParts.push(`productTag=${filters.tag.join(",")}`);
    }
    if (filters.brand.length > 0) {
      newQueryParts.push(`productBrand=${filters.brand.join(",")}`);
    }

    const attributeParts: string[] = [];
    for (const [taxonomy, terms] of Object.entries(filters.attributes)) {
      if (Array.isArray(terms) && terms.length > 0) {
        const cleanTaxonomy = taxonomy.replace(/^pa_/, "");
        const termsString = terms.join("_");
        // Use a colon ":" to separate the taxonomy from its terms
        attributeParts.push(`${cleanTaxonomy}:${termsString}`);
      }
    }
    if (attributeParts.length > 0) {
      newQueryParts.push(`productAttribute=${attributeParts.join(",")}`);
    }

    // --- 3. Sync Pagination & UI ---
    if (pagination.currentPage > 1) {
      newQueryParts.push(`productPage=${pagination.currentPage}`);
    }
    if (pagination.perPage !== 20) {
      newQueryParts.push(`productPerPage=${pagination.perPage}`);
    }
    if (ui.activeView !== "grid") {
      newQueryParts.push(`productView=${ui.activeView}`);
    }

    const newQueryString = newQueryParts.join("&");
    const newUrl = `${window.location.pathname}${
      newQueryString ? `?${newQueryString}` : ""
    }`;

    window.history.replaceState({ path: newUrl }, "", newUrl);
  }, [filters, pagination, ui, preserveParams]);
};
